/**
 * ClientMozDataPage — per-client MOZ DATA tab.
 *
 * Shows every domain monitored via /moz-api for this client, grouped by
 * property (business unit). Each monitor card displays the latest DA /
 * PA / Spam Score / linking-domain / external-link snapshot, a Refresh
 * button, and a full history table (every recorded snapshot, exportable
 * to CSV).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, RefreshCw, Download, Trash2, BarChart3, AlertTriangle,
  ExternalLink, ChevronDown, ChevronRight, Plus,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (v == null || v === "" ? "—" : String(v));
const scoreColor = (n) => {
  if (n == null) return "text-zinc-500";
  if (n >= 60) return "text-emerald-700";
  if (n >= 40) return "text-amber-700";
  if (n >= 20) return "text-orange-700";
  return "text-red-700";
};
const spamColor = (n) => {
  if (n == null) return "text-zinc-500";
  if (n <= 3) return "text-emerald-700";
  if (n <= 6) return "text-amber-700";
  return "text-red-700";
};

export default function ClientMozDataPage() {
  const { clientId } = useParams();
  const [monitors, setMonitors] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // monitor_id → snapshots
  const [snapshotsBy, setSnapshotsBy] = useState({}); // monitor_id → list
  const [refreshingId, setRefreshingId] = useState(null);
  const [addDomain, setAddDomain] = useState("");
  const [addBu, setAddBu] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [m, bu] = await Promise.all([
        api.get(`/moz/monitors?client_id=${clientId}`).then((r) => r.data),
        api.get(`/clients/${clientId}/business-units`).then((r) => r.data).catch(() => []),
      ]);
      setMonitors(m.monitors || []);
      setBusinessUnits(bu || []);
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const loadSnapshots = async (monitor_id) => {
    try {
      const { data } = await api.get(`/moz/monitors/${monitor_id}/snapshots`);
      setSnapshotsBy((p) => ({ ...p, [monitor_id]: data.snapshots || [] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  };

  const toggleExpand = (mid) => {
    setExpanded((p) => ({ ...p, [mid]: !p[mid] }));
    if (!snapshotsBy[mid]) loadSnapshots(mid);
  };

  const refreshMonitor = async (mid) => {
    setRefreshingId(mid);
    try {
      await api.post(`/moz/monitors/${mid}/refresh`);
      toast.success("Fresh snapshot taken");
      await refresh();
      await loadSnapshots(mid);
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshingId(null); }
  };

  const deleteMonitor = async (mid) => {
    if (!window.confirm("Delete this monitor and all its snapshots?")) return;
    try {
      await api.delete(`/moz/monitors/${mid}`);
      toast.success("Monitor removed");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const addMonitor = async () => {
    if (!addDomain.trim()) { toast.error("Enter a domain"); return; }
    setAddBusy(true);
    try {
      await api.post("/moz/monitors", {
        client_id: clientId,
        business_unit_id: addBu || null,
        domain: addDomain.trim(),
        scope: "root_domain",
        fetch_now: true,
      });
      toast.success("Monitor added — first snapshot fetched");
      setAddDomain(""); setAddBu("");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
    finally { setAddBusy(false); }
  };

  const exportSnapshotsCsv = (mid, monitor) => {
    const snaps = snapshotsBy[mid] || [];
    const cols = ["taken_at", "domain_authority", "page_authority",
      "spam_score", "linking_domains", "external_links"];
    const esc = (v) => v == null ? "" : /[",\n]/.test(String(v))
      ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const csv = [cols.join(",")].concat(
      snaps.map((r) => cols.map((c) => esc(r[c])).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `moz-${(monitor.domain || "monitor").replace(/[/:]/g, "_")}-history.csv`;
    a.click();
  };

  // Group monitors by business unit
  const grouped = useMemo(() => {
    const map = {};
    for (const m of monitors) {
      const key = m.business_unit_id || "__unassigned";
      map[key] = map[key] || [];
      map[key].push(m);
    }
    return map;
  }, [monitors]);

  const buName = (id) => {
    if (!id) return "Unassigned properties";
    return businessUnits.find((b) => b.business_unit_id === id)?.name
      || `Property ${id.slice(0, 8)}`;
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500">Loading MOZ data…</div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-4" data-testid="client-moz-data">
      <div>
        <div className="label-overline">SEO · Moz snapshots</div>
        <h1 className="font-display font-extrabold text-2xl mt-0.5 flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-[#002FA7]" /> MOZ Data
        </h1>
        <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
          Domain Authority, Page Authority and Spam Score for every domain
          being monitored for this client — auto-refreshed on the 1st of
          each month. Click any monitor to see the full snapshot history.
        </p>
      </div>

      {/* Add monitor */}
      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Domain to monitor
              </label>
              <input value={addDomain} onChange={(e) => setAddDomain(e.target.value)}
                placeholder="www.gbegroup.com.au"
                className="w-full mt-1 rounded-sm border border-zinc-300 px-2 py-1.5 text-sm font-mono"
                data-testid="moz-add-monitor-domain" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Property (optional)
              </label>
              <select value={addBu} onChange={(e) => setAddBu(e.target.value)}
                className="w-full mt-1 rounded-sm border border-zinc-300 px-2 py-1.5 text-sm"
                data-testid="moz-add-monitor-bu">
                <option value="">— No property —</option>
                {businessUnits.map((b) => (
                  <option key={b.business_unit_id} value={b.business_unit_id}>{b.name}</option>
                ))}
              </select>
            </div>
            <Button onClick={addMonitor} disabled={addBusy || !addDomain.trim()}
              className="bg-[#002FA7] hover:bg-[#001F7A] rounded-sm"
              data-testid="moz-add-monitor-btn">
              {addBusy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Adding…</>
                : <><Plus className="h-4 w-4 mr-1.5" /> Monitor</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {monitors.length === 0 && (
        <Card className="rounded-sm">
          <CardContent className="p-8 text-center">
            <BarChart3 className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
            <div className="text-sm text-zinc-500">
              No domains monitored yet. Add one above, or head to{" "}
              <a href="/moz-api" className="text-[#002FA7] underline">MOZ API</a>{" "}
              → Monitored domains.
            </div>
          </CardContent>
        </Card>
      )}

      {Object.keys(grouped).map((buId) => (
        <Card key={buId} className="rounded-sm">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b border-zinc-100">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Property</div>
              <div className="font-display font-bold">{buName(buId === "__unassigned" ? null : buId)}</div>
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">
                {grouped[buId].length} domain{grouped[buId].length === 1 ? "" : "s"}
              </Badge>
            </div>
            {grouped[buId].map((m) => (
              <div key={m.monitor_id} className="rounded-sm border border-zinc-200"
                data-testid={`moz-monitor-${m.monitor_id}`}>
                <div className="p-3 flex items-center gap-3 flex-wrap">
                  <button className="flex items-center gap-1 min-w-0"
                    onClick={() => toggleExpand(m.monitor_id)}
                    data-testid={`moz-monitor-toggle-${m.monitor_id}`}>
                    {expanded[m.monitor_id]
                      ? <ChevronDown className="h-4 w-4 text-zinc-400" />
                      : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                    <span className="font-mono font-semibold text-sm break-all">{m.domain}</span>
                    <a href={`https://${m.domain.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-zinc-400 hover:text-[#002FA7]">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </button>
                  <div className="flex-1 min-w-0" />
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <div className={`font-bold text-xl ${scoreColor(m.latest_domain_authority)}`}>
                        {fmt(m.latest_domain_authority)}
                      </div>
                      <div className="text-[9px] uppercase text-zinc-500">DA</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-bold text-xl ${scoreColor(m.latest_page_authority)}`}>
                        {fmt(m.latest_page_authority)}
                      </div>
                      <div className="text-[9px] uppercase text-zinc-500">PA</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-bold text-xl ${spamColor(m.latest_spam_score)}`}>
                        {m.latest_spam_score != null ? `${m.latest_spam_score}%` : "—"}
                      </div>
                      <div className="text-[9px] uppercase text-zinc-500">Spam</div>
                    </div>
                    <div className="text-center">
                      <div className="font-mono font-bold">{fmt(m.latest_linking_domains)}</div>
                      <div className="text-[9px] uppercase text-zinc-500">Linking</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] rounded-sm"
                      onClick={() => refreshMonitor(m.monitor_id)}
                      disabled={refreshingId === m.monitor_id}
                      data-testid={`moz-monitor-refresh-${m.monitor_id}`}>
                      {refreshingId === m.monitor_id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <><RefreshCw className="h-3 w-3 mr-1" /> Refresh</>}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600"
                      onClick={() => deleteMonitor(m.monitor_id)}
                      data-testid={`moz-monitor-delete-${m.monitor_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                {m.last_error && (
                  <div className="mx-3 mb-2 rounded-sm border border-red-200 bg-red-50 p-2 text-[11px] text-red-800 flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="font-mono break-all">{m.last_error}</span>
                  </div>
                )}
                {expanded[m.monitor_id] && (
                  <div className="border-t border-zinc-100 p-3 bg-zinc-50/40">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        Snapshot history · {(snapshotsBy[m.monitor_id] || []).length} entries
                      </div>
                      <Button size="sm" variant="outline"
                        className="h-6 px-2 text-[10px] rounded-sm"
                        onClick={() => exportSnapshotsCsv(m.monitor_id, m)}
                        disabled={!(snapshotsBy[m.monitor_id] || []).length}
                        data-testid={`moz-monitor-csv-${m.monitor_id}`}>
                        <Download className="h-3 w-3 mr-1" /> CSV
                      </Button>
                    </div>
                    {(snapshotsBy[m.monitor_id] || []).length === 0 ? (
                      <div className="text-[11px] italic text-zinc-500">
                        No snapshots yet — click Refresh above to take the first one.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                              <th className="py-1.5 pr-3">Taken</th>
                              <th className="py-1.5 pr-3">DA</th>
                              <th className="py-1.5 pr-3">PA</th>
                              <th className="py-1.5 pr-3">Spam</th>
                              <th className="py-1.5 pr-3">Linking domains</th>
                              <th className="py-1.5 pr-3">External links</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(snapshotsBy[m.monitor_id] || []).map((s) => (
                              <tr key={s.snapshot_id} className="border-b border-zinc-100">
                                <td className="py-2 pr-3 whitespace-nowrap text-zinc-500">
                                  {new Date(s.taken_at).toLocaleString()}
                                </td>
                                <td className={`py-2 pr-3 font-bold ${scoreColor(s.domain_authority)}`}>
                                  {fmt(s.domain_authority)}
                                </td>
                                <td className={`py-2 pr-3 font-bold ${scoreColor(s.page_authority)}`}>
                                  {fmt(s.page_authority)}
                                </td>
                                <td className={`py-2 pr-3 font-bold ${spamColor(s.spam_score)}`}>
                                  {s.spam_score != null ? `${s.spam_score}%` : "—"}
                                </td>
                                <td className="py-2 pr-3 font-mono">{fmt(s.linking_domains)}</td>
                                <td className="py-2 pr-3 font-mono">{fmt(s.external_links)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
